import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody } from "@material-tailwind/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExternalLinkAlt,
    faIdCard,
    faPhone,
    faUser,
    faCheckCircle,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

const DriverSummaryCard = ({ drivers, selectedDriver }) => {
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'AVAILABLE': return 'bg-green-100 text-green-800';
            case 'ASSIGNED': return 'bg-orange-100 text-orange-800';
            case 'OFF DUTY': return 'bg-gray-100 text-gray-800';
            case 'SUSPENDED': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getVerificationColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'verified': return 'bg-green-100 text-green-800';
            case 'failed': return 'bg-red-100 text-red-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (!selectedDriver) return null;

    // Find driver by ID (handling both string/number types)
    const driver = drivers.find(d =>
        (d.id_no && Number(d.id_no) === Number(selectedDriver)) ||
        (d.id && String(d.id) === String(selectedDriver))
    );

    if (!driver) return null;

    return (
        <Card className="shadow-md rounded-lg mb-6 bg-white border border-gray-100">
            <CardBody className="p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 border-b border-gray-100 pb-4">
                    <Link to={`/drivers/${driver.id_no}`} className="hover:opacity-80 transition-opacity">
                        <h3 className="text-xl font-bold text-[#2a2e30] flex items-center gap-2">
                            <FontAwesomeIcon icon={faUser} className="text-[#FF5733]" />
                            {driver.name}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                                <FontAwesomeIcon icon={faPhone} className="text-gray-400" />
                                {driver.phone}
                            </span>
                            <span className="text-gray-300">|</span>
                            <span className="flex items-center gap-1">
                                <FontAwesomeIcon icon={faIdCard} className="text-gray-400" />
                                {driver.id_no}
                            </span>
                        </div>
                    </Link>
                    <div className="flex flex-wrap gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getStatusColor(driver.status)}`}>
                            {driver.status || 'UNKNOWN'}
                        </span>
                        {driver.license_verification_status && (
                            <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${getVerificationColor(driver.license_verification_status)}`}>
                                {driver.license_verification_status === 'Verified' && <FontAwesomeIcon icon={faCheckCircle} />}
                                {driver.license_verification_status === 'Failed' && <FontAwesomeIcon icon={faExclamationTriangle} />}
                                {driver.license_verification_status}
                            </span>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* License Info */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">License Details</h4>
                        <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">License No:</span>
                                <span className="font-medium text-gray-900">{driver.license_no || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Expiry:</span>
                                <span className={`font-medium ${new Date(driver.license_expiry) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>
                                    {formatDate(driver.license_expiry)}
                                </span>
                            </div>

                            <div className="border-t border-gray-200 my-2 pt-2">
                                <div className="flex justify-between text-sm items-center">
                                    <span className="text-gray-600">Verification:</span>
                                    <span className={`font-medium px-2 py-0.5 rounded text-xs ${driver.license_verification_status?.toLowerCase() === 'verified'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-600'
                                        }`}>
                                        {driver.license_verification_status || 'Not Verified'}
                                    </span>
                                </div>

                                {driver.license_verified_on && (
                                    <div className="flex justify-between text-sm mt-1">
                                        <span className="text-gray-600">Verified On:</span>
                                        <span className="text-gray-900">{formatDate(driver.license_verified_on)}</span>
                                    </div>
                                )}

                                {(!driver.license_verification_status || driver.license_verification_status?.toLowerCase() !== 'verified' || new Date(driver.license_expiry) < new Date()) && (
                                    <div className="text-right mt-1">
                                        <Link
                                            to={`/drivers/${driver.id_no}`}
                                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                                        >
                                            Verify Now
                                        </Link>
                                    </div>
                                )}
                            </div>

                            {driver.license_sticker_link && (
                                <div className="pt-1">
                                    <a
                                        href={driver.license_sticker_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-xs text-purple-600 hover:text-purple-800 hover:underline"
                                    >
                                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                                        View License Sticker
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Financial & Assignment */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Status & Finance</h4>
                        <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Balance:</span>
                                <span className={`font-medium ${parseFloat(driver.bal) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {formatCurrency(driver.bal)}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Assigned Car:</span>
                                <span className="font-medium text-gray-900">{driver.assigned_plate || 'None'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Referred By:</span>
                                <span className="font-medium text-gray-900">{driver.referred_by || 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Actions / Links */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Quick Actions</h4>
                        <div className="flex flex-col gap-2">
                            <Link
                                to={`/drivers/${driver.id_no}`}
                                className="flex items-center justify-center gap-2 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 py-2 px-4 rounded-lg transition-colors border border-blue-200"
                            >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                View Driver Profile
                            </Link>
                            {driver.drive_link && (
                                <a
                                    href={driver.drive_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 text-sm bg-green-50 text-green-700 hover:bg-green-100 py-2 px-4 rounded-lg transition-colors border border-green-200"
                                >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                    Open Driver Folder
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default DriverSummaryCard;